import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, extractError } from "@/lib/api";
import { FAMILY } from "@/constants/testIds";
import { toast } from "sonner";
import { Loader2, Save, Trash2 } from "lucide-react";

const COMM_OPTIONS = ["verbal", "AAC", "PECS", "sign language", "gestures", "written"];
const ELOPE = ["none", "low", "moderate", "high"];

function Field({ label, testId, ...props }) {
  const Cmp = props.textarea ? "textarea" : "input";
  const { textarea, ...rest } = props;
  return (
    <div>
      <label className="field-label">{label}</label>
      <Cmp {...rest} data-testid={testId}
        rows={textarea ? 3 : undefined}
        className="w-full rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sage-500 outline-none" />
    </div>
  );
}

export default function ChildProfile() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    name: "", age: 0, diagnosis: "",
    communication_style: [], sensory_preferences: "",
    triggers: "", calming_strategies: "", interests: "",
    allergies: "", medications: "", toileting_needs: "",
    elopement_risk: "none", sleep_routine: "", feeding_preferences: "",
    behavioral_support_plan: "", maladaptive_behaviors: "", emergency_procedures: "",
  });

  useEffect(() => {
    if (isNew) return;
    api.get(`/children/${id}`).then(({ data }) => setF(data))
      .catch((e) => toast.error(extractError(e))).finally(() => setLoading(false));
  }, [id, isNew]);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const toggleComm = (v) => {
    const arr = f.communication_style || [];
    set("communication_style", arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  };

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...f, age: Number(f.age) || 0 };
      if (isNew) await api.post("/children", payload);
      else await api.put(`/children/${id}`, payload);
      toast.success("Child profile saved");
      navigate("/family");
    } catch (err) { toast.error(extractError(err)); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this child profile?")) return;
    try {
      await api.delete(`/children/${id}`);
      toast.success("Deleted");
      navigate("/family");
    } catch (err) { toast.error(extractError(err)); }
  };

  if (loading) return (<div className="min-h-screen bg-stone2-50"><Navbar /><div className="p-10 text-stone2-500">Loading…</div></div>);

  return (
    <div className="min-h-screen bg-stone2-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <span className="section-eyebrow">Child profile</span>
        <h1 className="mt-2 text-3xl font-heading font-bold text-sage-900">{isNew ? "Add a child" : `Edit ${f.name || "child"}`}</h1>

        <form onSubmit={submit} data-testid={FAMILY.childForm} className="mt-8 space-y-6">
          <section className="paper-card p-6 grid sm:grid-cols-2 gap-5">
            <Field label="First name" required value={f.name} onChange={(e) => set("name", e.target.value)} testId="child-name" />
            <Field label="Age" type="number" min="0" max="25" value={f.age} onChange={(e) => set("age", e.target.value)} testId="child-age" />
            <Field label="Diagnosis (optional)" value={f.diagnosis} onChange={(e) => set("diagnosis", e.target.value)} testId="child-diagnosis" />
            <div>
              <label className="field-label">Elopement risk</label>
              <select value={f.elopement_risk} onChange={(e) => set("elopement_risk", e.target.value)}
                data-testid="child-elopement"
                className="w-full rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-sm">
                {ELOPE.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="field-label">Communication style</label>
              <div className="flex flex-wrap gap-2">
                {COMM_OPTIONS.map((c) => {
                  const active = (f.communication_style || []).includes(c);
                  return (
                    <button key={c} type="button" onClick={() => toggleComm(c)}
                      data-testid={`child-comm-${c}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors duration-200 ${
                        active ? "bg-sage-600 text-white border-sage-600" : "bg-white border-stone2-200 text-sage-800 hover:border-sage-400"
                      }`}>
                      {c}
                    </button>
                  );
                })}
              </div>
            </div>
          </section>

          <section className="paper-card p-6 grid gap-5">
            <h2 className="font-heading font-semibold text-sage-900">Sensory, triggers & calming</h2>
            <Field textarea label="Sensory preferences" value={f.sensory_preferences} onChange={(e) => set("sensory_preferences", e.target.value)} testId="child-sensory" />
            <Field textarea label="Triggers" value={f.triggers} onChange={(e) => set("triggers", e.target.value)} testId="child-triggers" />
            <Field textarea label="Calming strategies" value={f.calming_strategies} onChange={(e) => set("calming_strategies", e.target.value)} testId="child-calming" />
            <Field textarea label="Interests & favorite activities" value={f.interests} onChange={(e) => set("interests", e.target.value)} testId="child-interests" />
          </section>

          <section className="paper-card p-6 grid sm:grid-cols-2 gap-5">
            <Field textarea label="Allergies" value={f.allergies} onChange={(e) => set("allergies", e.target.value)} testId="child-allergies" />
            <Field textarea label="Medications" value={f.medications} onChange={(e) => set("medications", e.target.value)} testId="child-medications" />
            <Field textarea label="Toileting needs" value={f.toileting_needs} onChange={(e) => set("toileting_needs", e.target.value)} testId="child-toileting" />
            <Field textarea label="Sleep routine" value={f.sleep_routine} onChange={(e) => set("sleep_routine", e.target.value)} testId="child-sleep" />
            <Field textarea label="Feeding preferences" value={f.feeding_preferences} onChange={(e) => set("feeding_preferences", e.target.value)} testId="child-feeding" />
          </section>

          <section className="paper-card p-6 grid gap-5">
            <h2 className="font-heading font-semibold text-sage-900">Behavioral support & emergency</h2>
            <Field textarea label="Behavioral support plan" value={f.behavioral_support_plan} onChange={(e) => set("behavioral_support_plan", e.target.value)} testId="child-bsp" />
            <Field textarea label="Examples of maladaptive behaviors" value={f.maladaptive_behaviors} onChange={(e) => set("maladaptive_behaviors", e.target.value)} testId="child-maladaptive" />
            <Field textarea label="Emergency procedures" value={f.emergency_procedures} onChange={(e) => set("emergency_procedures", e.target.value)} testId="child-emergency" />
          </section>

          <div className="flex justify-between">
            <div>
              {!isNew && (
                <button type="button" onClick={remove} data-testid={FAMILY.childDelete}
                  className="inline-flex items-center gap-2 text-danger text-sm hover:underline">
                  <Trash2 className="w-4 h-4" /> Delete child
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => navigate("/family")} className="btn-secondary">Cancel</button>
              <button type="submit" disabled={saving} data-testid={FAMILY.childSave} className="btn-primary">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
